import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Hotel, Clock, CheckCircle, XCircle, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_availability: { label: "Pending", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  available: { label: "Available", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  not_available: { label: "Not Available", color: "bg-rose-100 text-rose-700 border-rose-200", icon: XCircle },
  guest_confirmed: { label: "Guest Confirmed", color: "bg-violet-100 text-violet-700 border-violet-200", icon: AlertCircle },
  hotel_confirmed: { label: "Confirmed", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  completed: { label: "Completed", color: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-600 border-gray-200", icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending_availability;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${meta.color}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

export default function AdminHotels() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const fetchRows = async () => {
    try {
      const res = await axios.get(`${API}/hotel-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(res.data || []);
    } catch (e) {
      toast.error("Failed to load hotel requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    const poll = setInterval(fetchRows, 20000);
    return () => clearInterval(poll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    if (tab === "pending") return rows.filter(r => r.status === "pending_availability");
    if (tab === "available") return rows.filter(r => r.status === "available");
    if (tab === "guest_confirmed") return rows.filter(r => r.status === "guest_confirmed");
    if (tab === "confirmed") return rows.filter(r => r.status === "hotel_confirmed");
    if (tab === "completed") return rows.filter(r => r.status === "completed");
    if (tab === "closed") return rows.filter(r => ["not_available", "cancelled"].includes(r.status));
    return rows;
  }, [rows, tab]);

  const counts = {
    pending: rows.filter(r => r.status === "pending_availability").length,
    available: rows.filter(r => r.status === "available").length,
    guest_confirmed: rows.filter(r => r.status === "guest_confirmed").length,
    confirmed: rows.filter(r => r.status === "hotel_confirmed").length,
    completed: rows.filter(r => r.status === "completed").length,
    closed: rows.filter(r => ["not_available", "cancelled"].includes(r.status)).length,
  };

  const tabs = [
    { key: "pending", label: "Pending Availability", count: counts.pending, urgent: counts.pending > 0 },
    { key: "available", label: "Awaiting Agent", count: counts.available },
    { key: "guest_confirmed", label: "Awaiting Hotel Booking", count: counts.guest_confirmed, urgent: counts.guest_confirmed > 0 },
    { key: "confirmed", label: "Confirmed", count: counts.confirmed },
    { key: "completed", label: "Completed", count: counts.completed },
    { key: "closed", label: "Closed", count: counts.closed },
    { key: "all", label: "All", count: rows.length },
  ];

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Hotel className="w-7 h-7 text-emerald-600" /> Hotel Requests</h1>
          <p className="text-gray-500 text-sm">Manage agent hotel availability requests and confirmations</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all flex items-center gap-2 ${tab === t.key ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"} ${t.urgent && tab !== t.key ? "ring-2 ring-amber-300" : ""}`}
              data-testid={`admin-hotels-tab-${t.key}`}
            >
              {t.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === t.key ? "bg-white/20" : t.urgent ? "bg-amber-200 text-amber-900" : "bg-gray-100 text-gray-600"}`}>{t.count}</span>
            </button>
          ))}
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Hotel className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>No hotel requests in this view.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                      <th className="px-4 py-3">Request</th>
                      <th className="px-4 py-3">Agent</th>
                      <th className="px-4 py-3">Hotel</th>
                      <th className="px-4 py-3">City</th>
                      <th className="px-4 py-3">Dates</th>
                      <th className="px-4 py-3 text-right">Pax / Rooms</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <tr
                        key={r.id}
                        className="border-b border-gray-100 hover:bg-emerald-50/30 cursor-pointer"
                        onClick={() => navigate(`/admin/hotels/${r.id}`)}
                        data-testid={`admin-hotel-row-${r.id}`}
                      >
                        <td className="px-4 py-3 font-mono text-emerald-700 text-xs font-semibold">{r.request_number}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 text-xs">{r.agent_company || "—"}</p>
                          <p className="text-gray-500 text-xs">{r.agent_name}</p>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{r.hotel_name}</td>
                        <td className="px-4 py-3 text-gray-700">{r.city}</td>
                        <td className="px-4 py-3 text-gray-700">{r.check_in} → {r.check_out} <span className="text-xs text-gray-500">({r.nights}N)</span></td>
                        <td className="px-4 py-3 text-right text-gray-700">{r.pax_adults}A{r.pax_children > 0 ? ` + ${r.pax_children}C` : ""} / {r.rooms}R</td>
                        <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-semibold">{r.total_amount > 0 ? `₹${Number(r.total_amount).toLocaleString("en-IN")}` : "—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
